import {
  createTheme,
  ThemeOptions,
  PaletteOptions,
} from "@mui/material/styles";

// Palettes de couleurs
const warmPalette = {
  melon: "#ffa69e",
  eggshell: "#faf3dd",
  celeste: "#b8f2e6",
  lightBlue: "#aed9e0",
  paynesGray: "#5e6472",
};

const darkPalette = {
  richBlack: "#0d1b2a",
  oxfordBlue: "#1b263b",
  yinmnBlue: "#415a77",
  silverLakeBlue: "#778da9",
  platinum: "#e0e1dd",
};

export type PaletteType = "warm" | "dark";

// Interface pour les couleurs réutilisables
interface ReusableColors {
  primary: string;
  primaryHover: string;
  secondary: string;
  secondaryHover: string;
  third: string;
  thirdHover: string;
  background: string;
  surface: string;
  textPrimary: string;
  textSecondary: string;
}

// Extension des types MUI pour inclure nos couleurs personnalisées
declare module "@mui/material/styles" {
  interface Palette {
    customColors: {
      primary: string;
      secondary: string;
      accent: string;
      neutral: string;
      background: string;
    };
  }

  interface PaletteOptions {
    customColors?: {
      primary?: string;
      secondary?: string;
      accent?: string;
      neutral?: string;
      background?: string;
    };
  }
}

// Fonction pour créer la configuration de palette selon le type
const createPaletteOptions = (paletteType: PaletteType): PaletteOptions => {
  if (paletteType === "warm") {
    const colors = warmPalette;
    return {
      mode: "light",
      primary: {
        main: colors.lightBlue,
        light: colors.celeste,
        dark: colors.paynesGray,
        contrastText: "#ffffff",
      },
      secondary: {
        main: colors.melon,
        light: colors.eggshell,
        dark: "#cc6b63",
        contrastText: colors.paynesGray,
      },
      background: {
        default: colors.eggshell,
        paper: "#ffffff",
      },
      text: {
        primary: colors.paynesGray,
        secondary: "#747474",
      },
      customColors: {
        primary: colors.lightBlue,
        secondary: colors.melon,
        accent: colors.celeste,
        neutral: colors.paynesGray,
        background: colors.eggshell,
      },
    };
  } else {
    const colors = darkPalette;
    return {
      mode: "dark",
      primary: {
        main: colors.silverLakeBlue,
        light: colors.platinum,
        dark: colors.yinmnBlue,
        contrastText: colors.platinum,
      },
      secondary: {
        main: colors.yinmnBlue,
        light: colors.silverLakeBlue,
        dark: colors.oxfordBlue,
        contrastText: colors.platinum,
      },
      background: {
        default: colors.richBlack,
        paper: colors.oxfordBlue,
      },
      text: {
        primary: colors.platinum,
        secondary: colors.silverLakeBlue,
      },
      customColors: {
        primary: colors.silverLakeBlue,
        secondary: colors.yinmnBlue,
        accent: colors.platinum,
        neutral: colors.oxfordBlue,
        background: colors.richBlack,
      },
    };
  }
};

// Fonction pour créer les couleurs réutilisables selon le type de palette
const createReusableColors = (paletteType: PaletteType): ReusableColors => {
  if (paletteType === "warm") {
    const colors = warmPalette;
    return {
      primary: colors.lightBlue,
      primaryHover: colors.paynesGray,
      secondary: colors.melon,
      secondaryHover: "#cc6b63",
      third: colors.celeste,
      thirdHover: colors.paynesGray,
      background: colors.eggshell,
      surface: "#ffffff",
      textPrimary: colors.paynesGray,
      textSecondary: "#747474",
    };
  } else {
    const colors = darkPalette;
    return {
      primary: colors.silverLakeBlue,
      primaryHover: colors.yinmnBlue,
      secondary: colors.yinmnBlue,
      secondaryHover: colors.oxfordBlue,
      third: colors.platinum,
      thirdHover: colors.silverLakeBlue,
      background: colors.richBlack,
      surface: colors.oxfordBlue,
      textPrimary: colors.platinum,
      textSecondary: colors.silverLakeBlue,
    };
  }
};

// Fonction réutilisable pour créer les options de composants
const createComponentOptions = (colors: ReusableColors) => {
  return {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none" as const,
          fontWeight: 500,
        },
        containedPrimary: {
          backgroundColor: colors.primary,
          "&:hover": {
            backgroundColor: colors.primaryHover,
          },
        },
        containedSecondary: {
          backgroundColor: colors.secondary,
          "&:hover": {
            backgroundColor: colors.secondaryHover,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow:
            colors.background === warmPalette.eggshell
              ? "0 2px 8px rgba(94, 100, 114, 0.1)"
              : "0 4px 12px rgba(13, 27, 42, 0.3)",
          backgroundColor: colors.surface,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        filled: {
          backgroundColor: colors.third,
          color: colors.textPrimary,
        },
      },
    },
  };
};

// Fonction pour créer un thème complet
export const createCustomTheme = (paletteType: PaletteType) => {
  const textColor =
    paletteType === "warm" ? warmPalette.paynesGray : darkPalette.platinum;

  const reusableColors = createReusableColors(paletteType);

  const themeOptions: ThemeOptions = {
    palette: createPaletteOptions(paletteType),
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        color: textColor,
        fontWeight: 600,
      },
      h2: {
        color: textColor,
        fontWeight: 600,
      },
      h3: {
        color: textColor,
        fontWeight: 500,
      },
      body1: {
        color: textColor,
      },
    },
    components: createComponentOptions(reusableColors),
  };

  return createTheme(themeOptions);
};

// Thèmes par défaut
export const warmTheme = createCustomTheme("warm");
export const darkTheme = createCustomTheme("dark");

// Export des palettes pour utilisation directe
export { warmPalette, darkPalette };

// Thème par défaut
export default darkTheme;
